import React, {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
} from 'react';
import styled from 'styled-components';
import { Beneficiaries } from '../../../ReportBuilder/Components/Beneficiary/utils/beneficiaryInterfaces';
import {
  BeneficiaryRouteType,
  BeneficiaryRoutes,
} from '../../../ReportBuilder/Components/Beneficiary/BeneficiaryRoutes';
import { validateBeneficiaryNext } from '../../../ReportBuilder/Components/Beneficiary/BeneficiaryLanding';
import { FeedbackQuestionHandler, FeedbackSectionInfo } from '../utils';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import {
  useAllReportBeneficiaries,
  useBeneficiariesMutation,
} from 'shared/apollo/pageHooks/useAllReportBeneficiaries';

const questionsNeededForBeneficiaries = [40022, 40004, 40006];
const getAllAnswers = (questions: FeedbackSectionInfo['questions']) => {
  const result: {
    [id: number]: string | undefined;
  } = {};
  for (const id of questionsNeededForBeneficiaries) {
    result[id] = questions[id]?.text;
  }
  return result;
};

const StyledDiv = styled.div``;
const defaultBeneficiary = {
  age: undefined,
  ethnic: undefined,
  gender: undefined,
  health: undefined,
  social: undefined,
  work: undefined,
};

const Beneficiary = React.forwardRef<
  FeedbackQuestionHandler,
  FeedbackSectionInfo
>(
  (
    { reportId, questionId, questions, questionText, answer, initialRoute },
    forwardRef,
  ) => {
    const { run } = useBeneficiariesMutation();
    const {
      data,
      loading: beneficiariesLoading,
      error: beneficiariesError,
      refetch: beneficiariesRefetch,
    } = useAllReportBeneficiaries(reportId);

    const [oldState, setOldState] = useState<boolean>(false);

    const [beneficiaries, setBeneficiaries] = useState<Beneficiaries>(
      defaultBeneficiary,
    );

    const [route, setRoute] = useState<BeneficiaryRouteType>(
      initialRoute ||
        (beneficiaries && validateBeneficiaryNext('landing', beneficiaries)
          ? 'categories'
          : 'landing'),
    );

    useEffect(() => {
      if (oldState || beneficiariesLoading || beneficiariesError || !data)
        return;
      setOldState(true);
      setBeneficiaries(data);
      setRoute(
        initialRoute ||
          (validateBeneficiaryNext('landing', data) ? 'categories' : 'landing'),
      );
    }, [data, oldState, setOldState, setBeneficiaries, setRoute]);

    const handleRefresh = useCallback(() => {
      setOldState(false);
      beneficiariesRefetch();
    }, [beneficiariesRefetch, setOldState]);

    const handleBack = useCallback(() => {
      setRoute('landing');
    }, [setRoute]);

    useImperativeHandle(forwardRef, () => ({
      reportId,
      questionId,
      onCompleted: () => run(reportId, beneficiaries),
      onNextClicked: () => setRoute('categories'),
      onBackClicked: () => () =>
        route === 'categories' ? setRoute('landing') : undefined,
      answer: () => (route === 'landing' ? '' : JSON.stringify(beneficiaries)),
      errorText: () =>
        validateBeneficiaryNext(route, beneficiaries)
          ? undefined
          : 'Please add a valid beneficiary data.',
    }));

    if (beneficiariesLoading || !beneficiaries) {
      return <TrueImpactLoading />;
    }

    return (
      <StyledDiv>
        <BeneficiaryRoutes
          guidanceText={undefined}
          allAnswers={getAllAnswers(questions)}
          isMiniReportBuilder={true}
          route={route}
          error={beneficiariesError}
          loading={beneficiariesLoading}
          refetch={handleRefresh}
          beneficiaries={beneficiaries}
          setBeneficiaries={setBeneficiaries}
          goBack={handleBack}
          programName={'your program'}
        />
      </StyledDiv>
    );
  },
);

export default Beneficiary;
